import React from 'react';
import { Card, Button, Tooltip, Badge } from '@dopomogai/ui';
import { Info } from 'lucide-react';
import type { TierId, Range } from '@dopomogai/types';

// A simple helper for formatting money consistently within this component.
const formatMoney = (n: number) => `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

/**
 * The props for the SummaryPanel. It's a pure "data in, actions out" contract.
 */
type Props = {
  // --- Data props (provided by the `useTotals` hook in the parent) ---
  planTier: TierId;
  tierTooltip?: string; // Optional tooltip, e.g., "Senior required for Phone/IVR"
  setupTotalUSD: number;
  etaDays: Range;

  // --- Action props (callbacks from hooks like `useQuoteGenerator` in the parent) ---
  onGenerateQuote: () => void;
  onBookCall?: () => void;

  // --- State props (from the `useQuoteGenerator` hook in the parent) ---
  isLoading?: boolean;
};

/**
 * A live-updating summary of the configured agent plan and the final call-to-action.
 * This is a "dumb" presentational component that receives all its data and behavior via props,
 * making it highly reusable and easy to test.
 */
export function SummaryPanelWithCTAs({
  planTier,
  tierTooltip,
  setupTotalUSD,
  etaDays,
  onGenerateQuote,
  onBookCall,
  isLoading = false,
}: Props) {
  // Convert ETA from days to weeks for a more user-friendly display.
  const etaWeeks: Range = [etaDays[0] / 7, etaDays[1] / 7];

  // A reusable chunk of JSX for displaying the plan tier, which might have a tooltip.
  const planTierDisplay = (
    <div className="flex items-center gap-2">
      <div className="font-bold text-slate-900">Current Plan</div>
      <Badge text={planTier.toUpperCase()} accentColor="blue" />
    </div>
  );

  return (
    <Card className="space-y-4">
      {/* 
        Section 1: Current Plan Tier
        Conditionally wraps the display in a Tooltip if the parent provides a reason
        for the current tier (e.g., a feature required an upgrade).
      */}
      {tierTooltip ? (
        <Tooltip text={tierTooltip}>{planTierDisplay}</Tooltip>
      ) : (
        planTierDisplay
      )}

      {/* 
        Section 2: Key Metrics (Price and ETA)
        Displayed in a clean grid layout.
      */}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <div className="text-xs text-slate-600">Setup Total</div>
          <div className="text-xl font-extrabold">{formatMoney(setupTotalUSD)}</div>
        </div>
        <div className="space-y-1">
          <div className="text-xs text-slate-600">Est. ETA</div>
          <div className="text-xl font-extrabold">
            {etaWeeks[1] > 0
              ? `${etaWeeks[0].toFixed(1)}–${etaWeeks[1].toFixed(1)} wks`
              : 'Same day'}
          </div>
        </div>
      </div>

      {/*
        Section 3: Informational Disclaimer
      */}
      <div className="text-xs text-slate-600 flex items-start gap-1.5">
        <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
        <span>
          This is a one-time setup fee. Monthly usage costs are separate.
        </span>
      </div>

      {/* 
        Section 4: Calls to Action
        Buttons are wired to the callback props passed from the parent.
        Their disabled and loading states are controlled entirely by the parent.
      */}
      <div className="flex flex-col gap-2 pt-4 border-t border-slate-200">
        <Button
          variant="primary"
          onClick={onGenerateQuote}
          disabled={isLoading || setupTotalUSD <= 0}
          text={isLoading ? 'Generating...' : 'Generate CV / Quote'}
        />
        {onBookCall && (
          <Button
            variant="outline"
            onClick={onBookCall}
            disabled={isLoading}
            text="Book a Discovery Call"
          />
        )}
      </div>
    </Card>
  );
}